import React, { useState, useEffect } from "react";
import { FiInfo, FiCheck } from "react-icons/fi";
import "./Page3Properties.css";

export default function Page3Properties({ onNext, onPrev, propertiesData, initialPricingData, business_id }) {
  const [selectedProperty1, setSelectedProperty1] = useState(null);
  const [propertyData, setPropertyData] = useState({});
  const [property2Items, setProperty2Items] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showCostPrice, setShowCostPrice] = useState(initialPricingData?.showCostPrice || false);

  const isSimpleProduct = !propertiesData?.subProps1 || propertiesData.subProps1.length === 0;
  
  const DEFAULT_PROP1 = "کالای اصلی";
  const DEFAULT_PROP2 = "بدون ویژگی";

  const property1Items = isSimpleProduct ? [DEFAULT_PROP1] : propertiesData.subProps1;
  const property2Names = isSimpleProduct 
    ? [DEFAULT_PROP2] 
    : (propertiesData?.subProps2?.length > 0 ? propertiesData.subProps2 : []);

  const toPersianNumbers = (number) => {
    if (number === null || number === undefined) return '';
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    return number.toString().replace(/\d/g, (digit) => persianDigits[parseInt(digit)]);
  };

  const toEnglishNumbers = (persianNumber) => {
    if (persianNumber === null || persianNumber === undefined) return '';
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    return persianNumber.toString().replace(/[۰-۹]/g, (digit) => persianDigits.indexOf(digit));
  };

  const formatNumberWithCommas = (number) => {
    if (number === null || number === undefined) return '';
    const numStr = number.toString();
    const englishNumber = toEnglishNumbers(numStr);
    const formatted = englishNumber.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "،");
    return toPersianNumbers(formatted);
  };

  const ensureIdsForData = (data) => {
    let currentMaxId = 0;
    Object.values(data).forEach(items => {
      items.forEach(item => {
        if (item.id && typeof item.id === 'number') {
          if (item.id > currentMaxId) {
            currentMaxId = item.id;
          }
        }
      });
    });

    const newData = { ...data };
    Object.keys(newData).forEach(key => {
      newData[key] = newData[key].map(item => {
        if (!item.id) {
          currentMaxId++;
          return { ...item, id: currentMaxId };
        }
        return item;
      });
    });

    return newData;
  };

  const createNewItemTemplate = (name, isDefaultActive = false) => ({
    name,
    active: isDefaultActive, 
    price: "",
    rawPrice: "",
    costPrice: "",
    rawCostPrice: "",
    stock: ""
  });

  useEffect(() => {
    if (!isInitialized && property1Items.length > 0) {
      let finalData = {};
      const previousData = initialPricingData?.propertyData || {};

      property1Items.forEach(p1 => {
        const p2List = property2Names.length > 0 ? property2Names : [p1];
        
        finalData[p1] = p2List.map(p2Name => {
          const baseItem = createNewItemTemplate(p2Name, isSimpleProduct);
          
          if (previousData[p1]) {
            const existingRow = previousData[p1].find(row => row.name === p2Name);
            if (existingRow) {
              baseItem.active = existingRow.active !== undefined ? existingRow.active : baseItem.active;
              baseItem.price = existingRow.price || "";
              baseItem.rawPrice = existingRow.rawPrice || "";
              baseItem.costPrice = existingRow.costPrice || "";
              baseItem.rawCostPrice = existingRow.rawCostPrice || "";
              baseItem.stock = existingRow.stock || "";
              if (existingRow.id) baseItem.id = existingRow.id;
            }
          }
          return baseItem;
        });
      });

      Object.keys(finalData).forEach(key => {
        finalData[key] = finalData[key].map(item => {
          let formattedPrice = item.price;
          let rawPriceVal = item.rawPrice;
          let formattedCostPrice = item.costPrice;
          let rawCostPriceVal = item.rawCostPrice;
          let formattedStock = item.stock;

          const isPriceNumeric = typeof item.price === 'number' || (typeof item.price === 'string' && !isNaN(item.price) && !item.price.includes('،'));
          if (isPriceNumeric) {
            rawPriceVal = item.price.toString();
            formattedPrice = formatNumberWithCommas(rawPriceVal);
          }

          const isCostPriceNumeric = typeof item.costPrice === 'number' || (typeof item.costPrice === 'string' && !isNaN(item.costPrice) && !item.costPrice.includes('،'));
          if (isCostPriceNumeric) {
            rawCostPriceVal = item.costPrice.toString();
            formattedCostPrice = formatNumberWithCommas(rawCostPriceVal);
          }

          const isStockNumeric = typeof item.stock === 'number' || (typeof item.stock === 'string' && !isNaN(item.stock));
          if (isStockNumeric) {
            formattedStock = toPersianNumbers(item.stock);
          }

          return {
            ...item,
            price: formattedPrice,
            rawPrice: rawPriceVal ? String(rawPriceVal) : "",
            costPrice: formattedCostPrice,
            rawCostPrice: rawCostPriceVal ? String(rawCostPriceVal) : "",
            stock: formattedStock
          };
        });
      });

      const dataWithIds = ensureIdsForData(finalData);
      setPropertyData(dataWithIds);

      const previousSelectedProperty = initialPricingData?.selectedProperty1;
      const firstPropertyToSelect = property1Items.includes(previousSelectedProperty)
        ? previousSelectedProperty
        : property1Items[0];

      setSelectedProperty1(firstPropertyToSelect);
      setProperty2Items(dataWithIds[firstPropertyToSelect] || []);

      setIsInitialized(true);
    }
  }, [property1Items, property2Names, isInitialized, initialPricingData, isSimpleProduct]);

  const handleProperty1Select = (item) => {
    let updatedAllData = { ...propertyData };

    if (selectedProperty1) {
      updatedAllData[selectedProperty1] = property2Items;
    }

    if (!updatedAllData[item]) {
      const p2List = property2Names.length > 0 ? property2Names : [item];
      updatedAllData[item] = p2List.map(p2 => createNewItemTemplate(p2, isSimpleProduct));
      updatedAllData = ensureIdsForData(updatedAllData);
    }

    setPropertyData(updatedAllData);
    setSelectedProperty1(item);
    setProperty2Items(updatedAllData[item]);
  };

  const clearCostPriceIfNotSelected = (data) => {
    if (showCostPrice) return data;
    const newData = { ...data };
    Object.keys(newData).forEach(key => {
      newData[key] = newData[key].map(item => ({
        ...item,
        costPrice: "",
        rawCostPrice: ""
      }));
    });
    return newData;
  };

  const handlePrev = () => {
    let finalPropertyData = { ...propertyData };
    if (selectedProperty1) {
      finalPropertyData[selectedProperty1] = [...property2Items];
    }
    
    finalPropertyData = clearCostPriceIfNotSelected(finalPropertyData);

    const currentPricingData = {
      propertyData: finalPropertyData,
      selectedProperty1,
      propertiesData,
      isSimpleProduct,
      showCostPrice,
      summary: {
        totalProperty1: property1Items.length,
        totalProperty2: property2Items.length,
        activeProperty2: property2Items.filter(item => item.active).length
      }
    };

    onPrev(currentPricingData);
  };

  const handleToggle = (index) => {
    setProperty2Items(prev => {
      const newItems = [...prev];
      newItems[index] = {
        ...newItems[index],
        active: !newItems[index].active
      };
      return newItems;
    });
  };

  const handlePriceChange = (index, value) => {
    let numbersOnly = toEnglishNumbers(value).replace(/\D/g, '');
    numbersOnly = numbersOnly.length > 12 ? numbersOnly.substring(0, 12) : numbersOnly;
    const formattedValue = formatNumberWithCommas(numbersOnly);

    setProperty2Items(prev => {
      const newItems = [...prev];
      newItems[index] = {
        ...newItems[index],
        price: formattedValue,
        rawPrice: numbersOnly
      };
      return newItems;
    });
  };

  const handleCostPriceChange = (index, value) => {
    let numbersOnly = toEnglishNumbers(value).replace(/\D/g, '');
    numbersOnly = numbersOnly.length > 12 ? numbersOnly.substring(0, 12) : numbersOnly;
    const formattedValue = formatNumberWithCommas(numbersOnly);

    setProperty2Items(prev => {
      const newItems = [...prev];
      newItems[index] = {
        ...newItems[index],
        costPrice: formattedValue,
        rawCostPrice: numbersOnly
      };
      return newItems;
    });
  };

  const handleStockChange = (index, value) => {
    let numbersOnly = toEnglishNumbers(value).replace(/\D/g, '');
    numbersOnly = numbersOnly.length > 5 ? numbersOnly.substring(0, 5) : numbersOnly;
    const formattedValue = toPersianNumbers(numbersOnly);

    setProperty2Items(prev => {
      const newItems = [...prev];
      newItems[index] = {
        ...newItems[index],
        stock: formattedValue
      };
      return newItems;
    });
  };

  const handlePriceKeyDown = (e) => {
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
    if (!allowedKeys.includes(e.key) && !/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleStockKeyDown = (e) => {
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
    if (!allowedKeys.includes(e.key) && !/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handlePricePaste = (e, index) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const numbersOnly = toEnglishNumbers(pastedData).replace(/\D/g, '');
    const limitedNumbers = numbersOnly.length > 12 ? numbersOnly.substring(0, 12) : numbersOnly;
    const formattedValue = formatNumberWithCommas(limitedNumbers);

    setProperty2Items(prev => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], price: formattedValue, rawPrice: limitedNumbers };
      return newItems;
    });
  };

  const handleCostPricePaste = (e, index) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const numbersOnly = toEnglishNumbers(pastedData).replace(/\D/g, '');
    const limitedNumbers = numbersOnly.length > 12 ? numbersOnly.substring(0, 12) : numbersOnly;
    const formattedValue = formatNumberWithCommas(limitedNumbers);

    setProperty2Items(prev => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], costPrice: formattedValue, rawCostPrice: limitedNumbers };
      return newItems;
    });
  };

  const handleStockPaste = (e, index) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const numbersOnly = toEnglishNumbers(pastedData).replace(/\D/g, '');
    const limitedNumbers = numbersOnly.length > 5 ? numbersOnly.substring(0, 5) : numbersOnly;
    const formattedValue = toPersianNumbers(limitedNumbers);

    setProperty2Items(prev => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], stock: formattedValue };
      return newItems;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    let finalPropertyData = { ...propertyData };
    if (selectedProperty1) {
      finalPropertyData[selectedProperty1] = property2Items;
    }

    finalPropertyData = clearCostPriceIfNotSelected(finalPropertyData);

    const activeItems = property2Items.filter(item => item.active);

    if (activeItems.length === 0) {
      alert("لطفاً حداقل یک مورد را فعال کنید");
      return;
    }

    const hasEmptyPrices = activeItems.some(item =>
      item.rawPrice === null ||
      item.rawPrice === undefined ||
      String(item.rawPrice).trim() === ''
    );

    if (hasEmptyPrices) {
      alert("لطفاً قیمت فروش تمام موارد فعال را وارد کنید");
      return;
    }

    const formData = {
      propertyData: finalPropertyData,
      selectedProperty1,
      propertiesData,
      isSimpleProduct,
      showCostPrice,
      summary: {
        totalProperty1: property1Items.length,
        totalProperty2: property2Items.length,
        activeProperty2: activeItems.length,
        completedProperty1: Object.values(finalPropertyData).filter(propertyItems =>
          propertyItems.some(item => item.active && item.rawPrice && String(item.rawPrice).trim() !== '')
        ).length
      }
    };

    onNext(formData);
  };

  const getActiveItemsCount = () => {
    return property2Items.filter(item => item.active).length;
  };

  const getProperty1CompletionStatus = (item) => {
    const propertyItems = propertyData[item] || [];
    const activeItems = propertyItems.filter(propertyItem => propertyItem.active);

    const completedItems = activeItems.filter(propertyItem =>
      propertyItem.rawPrice !== null &&
      propertyItem.rawPrice !== undefined &&
      String(propertyItem.rawPrice).trim() !== ''
    );

    if (activeItems.length === 0) return 'empty';
    if (completedItems.length === activeItems.length) return 'completed';
    if (completedItems.length > 0) return 'partial';
    return 'empty';
  };

  const getStatusIcon = (item) => {
    const status = getProperty1CompletionStatus(item);
    switch (status) {
      case 'completed': return '🟢';
      case 'partial': return '🟡';
      case 'empty': return '⚪';
      default: return '⚪';
    }
  };

  return (
    <div className="p3-container">
      {/* هدر */}
      <div className="p3-header-container">
        <h2 className="p3-title">قیمت‌گذاری و موجودی</h2>
        <button 
          className={`p3-help-icon-btn ${showHelp ? 'active' : ''}`}
          onClick={() => setShowHelp(!showHelp)}
          title="راهنمای قیمت‌گذاری"
        >
          <FiInfo size={20} />
        </button>
      </div>

      {/* باکس راهنما */}
      {showHelp && (
        <div className="p3-help-box">
            <p className="p3-description">
            ویژگی‌هایی که موجود دارید را فعال کرده و برای آن‌ها <strong>قیمت فروش</strong> و <strong>موجودی</strong> تعیین کنید. مقادیر قیمت به تومان است. همچنین در صورت تمایل می‌توانید بهای تمام شده (قیمت خرید) را وارد کنید تا در گزارش‌های سود و زیان لحاظ گردد.
          </p>
        </div>
      )}

      {/* انتخاب خصوصیت اول */}
      {!isSimpleProduct && (
        <div className="p3-color-selection-section">
          <h3 className="p3-section-title">
            انتخاب {propertiesData?.title1 || 'خصوصیت اول'}
          </h3>
          <div className="p3-color-buttons">
            {property1Items.map(item => (
              <button
                key={item}
                type="button"
                className={`p3-color-btn ${selectedProperty1 === item ? 'p3-color-active' : (
                  getProperty1CompletionStatus(item) === 'completed' ? 'p3-color-completed' : (getProperty1CompletionStatus(item) === 'partial' ? 'p3-color-partial' : 'p3-color-empty')
                )}`}
                onClick={() => handleProperty1Select(item)}
              >
                <span className="p3-color-text">
                  {getStatusIcon(item)} {item}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedProperty1 && (
        <div className="p3-table-section">
          {/* استایل چیپسی و لمسی مناسب موبایل برای تیک بهای تمام شده */}
          <div className="p3-costprice-bar">
            <label className={`p3-chip-toggle ${showCostPrice ? 'checked' : ''}`}>
              <input 
                type="checkbox" 
                checked={showCostPrice}
                onChange={(e) => setShowCostPrice(e.target.checked)}
              />
              <span className="p3-chip-box">
                {showCostPrice && <FiCheck className="p3-chip-check-icon" />}
              </span>
              <span className="p3-chip-label">ثبت بهای تمام شده (خرید)</span>
            </label>
          </div>

          <div className="p3-table-container">
            <table className="p3-table">
              <thead>
                <tr>
                  <th className="p3-th p3-th-title">
                    {isSimpleProduct ? 'کالا' : (propertiesData?.title2 || 'عنوان')}
                  </th>
                  {showCostPrice && (
                    <th className="p3-th p3-th-price p3-th-cost">
                      <span className="p3-th-full">بهای تمام‌شده</span>
                      <span className="p3-th-short">خرید</span>
                    </th>
                  )}
                  <th className="p3-th p3-th-price">
                    <span className="p3-th-full">قیمت فروش</span>
                    <span className="p3-th-short">فروش</span>
                  </th>
                  <th className="p3-th p3-th-stock">موجودی</th>
                  <th className="p3-th p3-th-status">وضعیت</th>
                </tr>
              </thead>
              <tbody>
                {property2Items.map((item, index) => (
                  <tr key={item.id || index} className="p3-tr">
                    <td className="p3-td p3-td-title">
                      <span className="p3-size-title">
                        {isSimpleProduct ? 'کالای اصلی' : item.name}
                      </span>
                    </td>

                    {/* ستون بهای تمام شده (قبل از فروش) */}
                    {showCostPrice && (
                      <td className="p3-td p3-td-price p3-td-cost">
                        <div className="p3-input-wrapper">
                          <input
                            type="text"
                            inputMode="numeric"
                            className="p3-input p3-price-input p3-cost-input"
                            placeholder="۰"
                            value={item.costPrice}
                            onChange={(e) => handleCostPriceChange(index, e.target.value)}
                            onKeyDown={handlePriceKeyDown}
                            onPaste={(e) => handleCostPricePaste(e, index)}
                            disabled={!item.active}
                          />
                        </div>
                      </td>
                    )}

                    {/* ستون قیمت فروش */}
                    <td className="p3-td p3-td-price">
                      <div className="p3-input-wrapper">
                        <input
                          type="text"
                          inputMode="numeric"
                          className="p3-input p3-price-input"
                          placeholder="۰"
                          value={item.price}
                          onChange={(e) => handlePriceChange(index, e.target.value)}
                          onKeyDown={handlePriceKeyDown}
                          onPaste={(e) => handlePricePaste(e, index)}
                          disabled={!item.active}
                        />
                      </div>
                    </td>

                    <td className="p3-td p3-td-stock">
                      <input
                        type="text"
                        inputMode="numeric"
                        className="p3-input p3-stock-input"
                        placeholder="۰"
                        value={item.stock}
                        onChange={(e) => handleStockChange(index, e.target.value)}
                        onKeyDown={handleStockKeyDown}
                        onPaste={(e) => handleStockPaste(e, index)}
                        disabled={!item.active}
                      />
                    </td>

                    <td className="p3-td p3-td-status">
                      <div
                        className={`p3-simple-toggle ${item.active ? 'active' : ''}`}
                        onClick={() => handleToggle(index)}
                      >
                        {item.active ? 'فعال' : 'غیرفعال'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!isSimpleProduct && (
            <div className="p3-color-summary">
              <p>
                وضعیت <strong>{selectedProperty1}</strong>:
                {getProperty1CompletionStatus(selectedProperty1) === 'completed' && ' 🟢 تکمیل شده'}
                {getProperty1CompletionStatus(selectedProperty1) === 'partial' && ' 🟡 ناقص'}
                {getProperty1CompletionStatus(selectedProperty1) === 'empty' && ' ⚪ خالی'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* کلیدهای ناوبری */}
      <div className="p3-button-wrapper">
        <button
          type="button"
          className="p3-prev-btn"
          onClick={handlePrev}
        >
          <span className="p3-btn-icon">→</span>
          مرحله قبل
        </button>

        <button
          type="button"
          className="p3-next-btn"
          onClick={handleSubmit}
          disabled={getActiveItemsCount() === 0}
        >
          مرحله بعد
          <span className="p3-btn-icon">←</span>
        </button>
      </div>
    </div>
  );
}
